'use client'

import React from 'react'
import type { ComparisonTableBlock as ComparisonTableBlockProps } from '@/payload-types'
import { cn } from '@/utilities/ui'
import { getBlockPadding } from '@/utilities/getBlockPadding'
import {
  ScrollableTable,
  LabelCell,
  LogoCell,
  TextCell,
  TextLargeCell,
  getCellBackgroundStyles,
} from '@/components/tables'
import RichText from '@/components/RichText'
import type { BrandLogo, CellBackgroundColor } from '@/components/tables'

type Props = ComparisonTableBlockProps & {
  disableInnerContainer?: boolean
}

interface Provider {
  logo?: string | null
  subheading?: string | null
  id?: string | null
}

interface ComparisonValue {
  value?: string | null
  secondaryText?: string | null
  isLarge?: boolean | null
  backgroundColor?: CellBackgroundColor | null
  id?: string | null
}

interface ComparisonRow {
  rowLabel?: string | null
  rowLabelSecondary?: string | null
  values?: ComparisonValue[] | null
  id?: string | null
}

// Helper to determine cell background class for highlighted columns
const getHighlightedCellBg = (
  isHighlighted: boolean,
  backgroundColor: CellBackgroundColor | undefined,
  bgClass: string,
): string => {
  if (!isHighlighted) return bgClass
  if (backgroundColor === 'black') return 'bg-black'
  return 'bg-civo-lime'
}

// Helper to determine text background color for cell components
const getCellTextBg = (
  isHighlighted: boolean,
  backgroundColor: CellBackgroundColor | undefined,
): CellBackgroundColor | undefined => {
  if (!isHighlighted) return backgroundColor
  if (backgroundColor === 'black') return 'black'
  return 'lime'
}

export const ComparisonTableBlock: React.FC<Props> = ({
  caption,
  highlightedColumnIndex,
  providers,
  rows,
  tableNote,
  tableNoteAlignment = 'left',
  paddingTop,
  paddingBottom,
}) => {
  const paddingClasses = getBlockPadding({
    paddingTop: paddingTop ?? true,
    paddingBottom: paddingBottom ?? true,
  })

  const allProviders = (providers as Provider[]) || []
  const allRows = (rows as ComparisonRow[]) || []

  // Convert 1-based index to 0-based (and account for the label column)
  const highlightIndex = highlightedColumnIndex ? highlightedColumnIndex - 1 : -1

  // Check if any provider has a subheading
  const hasAnySubheading = allProviders.some((provider) => !!provider.subheading)

  if (allProviders.length < 2 || allRows.length === 0) {
    return null
  }

  return (
    <div className={cn('w-full', paddingClasses)}>
      <ScrollableTable>
        <table
          className="w-full border-separate border-spacing-0 table-fixed"
          style={{ minWidth: `${(allProviders.length + 1) * 160}px` }}
        >
          {caption && <caption className="sr-only">{caption}</caption>}

          {/* Header rows with provider logos and subheadings */}
          <thead>
            {/* Logo row */}
            <tr>
              {/* Empty header for label column */}
              <th
                scope="col"
                rowSpan={hasAnySubheading ? 2 : 1}
                className={cn(
                  'text-left align-middle',
                  'px-7 py-5 max-xl:px-5 max-xl:py-4',
                  'border-b border-black',
                )}
              >
                <span className="sr-only">Feature</span>
              </th>

              {/* Provider logo headers */}
              {allProviders.map((provider, providerIndex) => {
                const isHighlighted = providerIndex === highlightIndex
                const isLastColumn = providerIndex === allProviders.length - 1

                return (
                  <th
                    key={provider.id || providerIndex}
                    scope="col"
                    className={cn(
                      'text-center align-middle',
                      'px-7 py-6 max-xl:px-5 max-xl:py-5',
                      'border-t border-t-black',
                      'border-b border-black',
                      !isLastColumn && 'border-r border-r-black',
                      isHighlighted && 'bg-civo-lime',
                    )}
                  >
                    <LogoCell logo={provider.logo as BrandLogo} />
                  </th>
                )
              })}
            </tr>

            {/* Subheading row - only shown if at least one provider has a subheading */}
            {hasAnySubheading && (
              <tr>
                {allProviders.map((provider, providerIndex) => {
                  const isHighlighted = providerIndex === highlightIndex
                  const isLastColumn = providerIndex === allProviders.length - 1

                  return (
                    <th
                      key={`subheading-${provider.id || providerIndex}`}
                      scope="col"
                      className={cn(
                        'text-center align-middle break-words',
                        'p-3',
                        'border-b border-black',
                        !isLastColumn && 'border-r border-r-black',
                        isHighlighted ? 'bg-civo-lime' : 'bg-civo-cloud',
                      )}
                    >
                      {provider.subheading && (
                        <span className="body-copy-small font-medium text-black break-words">{provider.subheading}</span>
                      )}
                    </th>
                  )
                })}
              </tr>
            )}
          </thead>

          {/* Body rows */}
          <tbody>
            {allRows.map((row, rowIndex) => {
              const values = row.values || []

              // Check if label cell is blank
              const hasLabel = !!row.rowLabel || !!row.rowLabelSecondary

              return (
                <tr
                  key={row.id || rowIndex}
                  className="group"
                >
                  {/* Label cell */}
                  <th
                    scope="row"
                    className={cn(
                      'text-left align-top',
                      'px-7 py-7 max-xl:px-5 max-xl:py-5',
                      'transition-colors duration-200',
                      hasLabel && 'border-r border-r-black',
                      hasLabel && 'border-b border-black',
                      hasLabel && 'group-hover:bg-black/5',
                    )}
                  >
                    {hasLabel && (
                      <LabelCell label={row.rowLabel || ''} description={row.rowLabelSecondary} />
                    )}
                  </th>

                  {/* Value cells */}
                  {allProviders.map((_, providerIndex) => {
                    const cellValue = values[providerIndex]
                    const isHighlighted = providerIndex === highlightIndex
                    const isLastColumn = providerIndex === allProviders.length - 1
                    const backgroundColor = cellValue?.backgroundColor as CellBackgroundColor | undefined
                    const { bgClass } = getCellBackgroundStyles(backgroundColor)
                    const hasCellContent = !!cellValue?.value || !!cellValue?.secondaryText

                    // Determine hover background based on cell state
                    const getHoverBg = () => {
                      if (!hasCellContent) return 'group-hover:bg-black/5'
                      if (backgroundColor === 'black') return '' // No hover on black cells
                      if (isHighlighted) return 'group-hover:bg-civo-lime/80'
                      if (backgroundColor && backgroundColor !== 'default') return '' // No hover on colored cells
                      return 'group-hover:bg-black/5'
                    }

                    return (
                      <td
                        key={providerIndex}
                        className={cn(
                          'text-center align-top',
                          'px-7 py-7 max-xl:px-5 max-xl:py-5',
                          'transition-colors duration-200',
                          hasCellContent && getHighlightedCellBg(isHighlighted, backgroundColor, bgClass),
                          hasCellContent && 'border-b border-black',
                          hasCellContent && !isLastColumn && 'border-r border-r-black',
                          getHoverBg(),
                        )}
                      >
                        {hasCellContent && (
                          cellValue?.isLarge ? (
                            <TextLargeCell
                              primary={cellValue.value || ''}
                              secondary={cellValue.secondaryText}
                              backgroundColor={getCellTextBg(isHighlighted, backgroundColor)}
                              align="center"
                            />
                          ) : (
                            <TextCell
                              primary={cellValue?.value || ''}
                              secondary={cellValue?.secondaryText}
                              backgroundColor={getCellTextBg(isHighlighted, backgroundColor)}
                              align="center"
                            />
                          )
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </ScrollableTable>

      {/* Table Note */}
      {tableNote && (
        <div
          className={cn(
            'mt-6 w-full body-copy-small text-black/70 [&_strong]:text-black [&_b]:text-black',
            tableNoteAlignment === 'center' && 'text-center',
            tableNoteAlignment === 'right' && 'text-right',
          )}
        >
          <RichText data={tableNote} enableProse={false} enableGutter={false} />
        </div>
      )}
    </div>
  )
}
